import { TaskWorkspace } from "../components/TaskWorkspace";
import { WorkspaceEvents } from "../components/WorkspaceEvents";
import { RecoveryControls, RecoveryPoints, RestartTaskButton } from "../components/RecoveryControls";
import { ExtractionPanel, extractionBlocked } from "../components/ExtractionPanel";
import { RunPulse } from "../components/RunPulse";
// 任务工作台：左上实时画面、左下功能 tabs，右侧事件与操作。
// 扫码/审核保持原权限，恢复与重启由用户点击；旧运行和材料保留。
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isScanBlockReason, type Priority, type TaskDTO, type TaskTypeDTO } from "@csp/shared";
import { api, isStaleConflict } from "../lib/api";
import { useAlerts } from "../lib/alerts";
import { useMediaToken } from "../lib/media";
import { isStaff, useAuth } from "../lib/auth";
import { fmtDateTime, fmtDur, fmtTime, PRIORITY_TEXT, REMOVE_HINT } from "../lib/format";
import { useAction, useInterval, useLoad } from "../lib/hooks";
import { apiUrl, mediaUrl } from "../lib/net";
import { useWsMessages } from "../lib/realtime";
import type { ProposalListItem, RunDetail } from "../lib/types";
import { Badge, DryBadge, PriorityBadge, ProgressBar, StatusBadge } from "../components/Badges";
import { useDialog } from "../components/Dialogs";
import { useLightbox } from "../components/Lightbox";
import { SopSupplements } from "../components/SopSupplements";
import { FileBoard } from "../components/FileBoard";
import { MaterialsPanel } from "../components/Materials";
import { ExplorationPreparation } from "../components/ExplorationPreparation";
import { StandbyBadge, StandbyScreen } from "../components/StandbyScreen";
import { AssigneePicker } from "../components/AssigneePicker";
import { ExplorationQuestion } from "../components/ExplorationQuestion";
import { ExecutionJournalHistory } from "../components/ExecutionJournal";
import { ProposalCard } from "../components/ProposalCard";
import { HoldButtons, ReviewPanel } from "../components/ReviewPanel";
import { ScanPanel } from "../components/ScanPanel";
import { StepTimeline } from "../components/StepTimeline";
import { StuckPanel } from "../components/StuckPanel";
import { VncViewer } from "../components/VncViewer";
import { SponsorMailLine, sponsorEmailOf } from "../components/SponsorMail";

const CLOSED = ["done", "failed", "cancelled", "archived"];

type Tab = "overview" | "controls" | "steps" | "files" | "data" | "sop" | "points";

/** 这些状态下提示还缺什么才能开始 / 重新开始 */
const RESTARTABLE_HINT = ["draft", "ready", "blocked", "failed"];

export function TaskDetailPage() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dialog = useDialog();
  const act = useAction();
  const { conflict, toast } = useAlerts();
  const lb = useLightbox();
  const staff = isStaff(user?.role);

  const token = useMediaToken();
  const taskQ = useLoad(() => api.task(id), [id]);
  const task = taskQ.data;
  const typeQ = useLoad<TaskTypeDTO | null>(() => (task ? api.type(task.typeId) : Promise.resolve(null)), [task?.typeId]);
  const runId = task?.currentRunId ?? null;
  const runQ = useLoad<RunDetail | null>(() => (runId ? api.run(runId) : Promise.resolve(null)), [runId]);
  const run = runQ.data;
  const proposalId = run?.proposalId ?? null;
  const propQ = useLoad<ProposalListItem | null>(
    async () => {
      if (!proposalId || !staff || !task) return null;
      return api.proposal(proposalId);
    },
    [proposalId, staff, task?.typeId],
  );
  // 扫码成功：后端判定登录后写 scan_login 事件并转运行，扫码弹框随之卸载，这里补一句提示。
  // 以首次加载时最新的那条为基线，只对之后新出现的提示一次（点重新登录引起的转运行没有这条事件）
  const lastScanLogin = task?.timeline.filter((e) => e.kind === "scan_login").at(-1)?.at ?? "";
  const scanLoginSeen = useRef<string | null>(null);
  useEffect(() => {
    if (!task) return;
    if (scanLoginSeen.current !== null && lastScanLogin && lastScanLogin !== scanLoginSeen.current) toast("ok", "扫码成功，已自动继续运行");
    scanLoginSeen.current = lastScanLogin;
  }, [task, lastScanLogin, toast]);
  const [live, setLive] = useState(true);
  const [tab, setTab] = useState<Tab | null>(null);

  useEffect(() => {
    if (task && ["waiting_scan", "blocked", "review_required", "done"].includes(task.status)) setTab("controls");
  }, [task?.status]);
  // 还没跑过、文件或字段没齐：先回全屏「准备文件」
  useEffect(() => {
    if (task && task.status === "draft" && !task.currentRunId && !task.demo) navigate(`/tasks/${task.id}/prepare`, { replace: true });
  }, [task?.id, task?.status, task?.currentRunId]);

  // 实时：该任务有变化就重拉任务和当前运行（合并抖动）
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void taskQ.reload();
      void runQ.reload();
      void propQ.reload();
    }, 250);
  }, [taskQ, runQ, propQ]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  useWsMessages((m) => {
    if ((m.type === "task" || m.type === "alert") && m.taskId === id) refresh();
  });

  // 兜底轮询（实时事件丢失时）：执行中每 30 秒拉一次
  const activeNow = !!task && ["queued", "running", "waiting_scan", "review_required"].includes(task.status);
  useInterval(refresh, activeNow ? 30_000 : null);

  const labels = useMemo(() => {
    const o: Record<string, string> = {};
    for (const f of typeQ.data?.manifestSchema.fields ?? []) o[f.key] = f.label;
    return o;
  }, [typeQ.data]);

  if (taskQ.error && !task) return <div className="notice err">{taskQ.error}</div>;
  if (!task || !user) return <div className="empty">加载中…</div>;

  const reload = () => {
    void taskQ.reload();
    void runQ.reload();
    void propQ.reload();
  };
  const doAct = async (name: string, fn: () => Promise<unknown>) => {
    const r = await act.run(name, fn);
    if (r !== undefined) reload();
  };
  const cancel = async () => {
    const note = await dialog.prompt({
      title: `取消任务 #${task.no}？`,
      body: <p>正在执行的会被中止，浏览器标签页会关闭。</p>,
      label: "原因（可不填）",
      okText: "确认取消",
      cancelText: "不取消",
      danger: true,
    });
    if (note !== null) await doAct("cancel", () => api.cancelTask(task.id, note || undefined));
  };
  const remove = async () => {
    if (await dialog.confirm({ title: `删除任务 #${task.no}？`, body: <p>{REMOVE_HINT}</p>, okText: "删除", danger: true })) {
      const r = await act.run("remove", () => api.removeTask(task.id));
      if (r) navigate("/");
    }
  };
  const changePriority = async (p: Priority) => {
    await doAct("priority", async () => {
      try {
        return await api.setPriority(task.id, p, task.updatedAt);
      } catch (e) {
        if (isStaleConflict(e)) conflict(reload);
        throw e;
      }
    });
  };

  const st = task.status;
  // 探索任务还没跑过：缺字段时是 draft，齐了是 ready，都在准备页
  const preparing = !!task.learningRun && (st === "ready" || st === "draft") && !task.currentRunId;
  const startBlocked = extractionBlocked(task) ?? (task.browserKind === "corppass" && !task.assignee ? "请先选谁来扫码登录 Corppass" : null);
  // 概览和事件记录右上角都有「开始」：就绪（含探索任务）或失败后重来
  // dry run（含探索）做完没提交：可以再跑一次（从头重启，门户草稿还在会接着用）
  const againDry = st === "done" && !!task.dryRun;
  const canStart = st === "ready" || st === "failed" || againDry;
  // 还没开始（含排队）：实时画面看待命浏览器
  const waitingStart = !task.demo && ["draft", "ready", "queued"].includes(st) && !task.currentRunId;
  const start = () => doAct("start", () => (againDry ? api.restartTask(task.id) : api.startTask(task.id)));
  // 失败的任务也能换材料后重新开始；执行中不能删
  const canEditMaterials = againDry || !["done", "cancelled", "archived", "running", "waiting_scan", "review_required"].includes(st);
  const canUrgent = user.role !== "clerk";
  const priorities: Priority[] = canUrgent ? ["low", "normal", "high", "urgent"] : ["low", "normal", "high"];
  const lastShot = run ? (run.lastScreenshotUrl ?? [...run.steps].reverse().find((s) => s.screenshotUrl)?.screenshotUrl ?? null) : null;
  const showRecording = !!run && !!token && run.recordingAvailable && (run.endedAt !== null || st === "blocked");
  const playerSrc = run
    ? `/player.html?run=${encodeURIComponent(run.id)}&base=${encodeURIComponent(apiUrl("/api/evidence/"))}&token=${encodeURIComponent(token ?? "")}`
    : "";
  const reviewInTakeover = task.pendingReview?.status === "takeover";
  const cardClass = st === "waiting_scan" ? "attn-scan" : st === "review_required" ? "attn-review" : st === "blocked" ? "attn-blocked" : "";

  // 没手动选过 tab 时：有运行看步骤，没运行（草稿）看材料
  const curTab: Tab = tab ?? (run ? "steps" : "files");
  const TABS: { key: Tab; label: string; warn?: boolean; count?: number }[] = [
    { key: "overview", label: "概览" },
    { key: "controls", label: "处理与结果" },
    { key: "steps", label: "步骤与录像", count: run?.steps.length || undefined },
    { key: "files", label: "材料", warn: st === "draft" && task.missing.length > 0, count: task.files.length || undefined },
    { key: "data", label: "任务资料" },
    { key: "sop", label: "SOP 修订" },
    { key: "points", label: "已知卡点" },
  ];

  return (
    <TaskWorkspace
      screen={<><div className="workspace-screen-title"><b>实时画面</b>{waitingStart ? <StandbyBadge standby={task.standby} /> : <span className="row" style={{ gap: 8, alignItems: "center" }}>{st === "review_required" && task.pendingReview?.hold && staff && <HoldButtons small task={task} review={task.pendingReview} takeover={!!run?.takeover} onChanged={reload} />}<RunPulse status={st} lastAt={task.timeline.at(-1)?.at ?? null} runNo={run?.attemptNo ?? null} /></span>}</div>{waitingStart ? <StandbyScreen task={task} canAct={staff} live={live} onChanged={reload} /> : live && staff && ["running","waiting_scan","review_required","blocked"].includes(st) ? <VncViewer taskId={task.id} mode={run?.takeover ? "control" : "view"} height={420} /> : lastShot ? <img className="workspace-last-shot" src={mediaUrl(lastShot,token) ?? undefined} alt="最后运行画面" onClick={() => { const src=mediaUrl(lastShot,token); if(src) lb.open(src); }} /> : <div className="workspace-screen-empty">任务开始后，这里显示实时操作画面</div>}</>}
      tabs={<div className="tabs" data-testid="detail-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={`${curTab === t.key ? "active" : ""}${t.warn ? " warn" : ""}`} onClick={() => setTab(t.key)} aria-pressed={curTab === t.key}>
            {t.label}
            {t.count !== undefined && <span className="muted small"> {t.count}</span>}
          </button>
        ))}
      </div>}
      events={<WorkspaceEvents task={task} run={run ?? null} canEdit={user.role === "admin"} canInterrupt={staff} onChanged={reload} onSop={() => setTab("sop")}
        action={<><StatusBadge status={st} />{canStart && <button className="btn btn-primary btn-sm" data-testid="start-task-events" disabled={!!act.busy || !!startBlocked} title={startBlocked ?? undefined} onClick={() => void start()}>{act.busy === "start" ? "开始中…" : againDry ? "▶ 再跑一次" : st === "failed" ? "▶ 重新开始" : "▶ 开始任务"}</button>}</>} />}
    >
      {/* 等你回答（验证码 / 缺字段 / 选下一步）：放在所有标签页外面，弹框关了也能随时点开 */}
      {st === "review_required" && task.pendingReview?.hold && staff && (
      <div className="card attn-scan" data-testid="hold-bar">
        <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <b>{run?.takeover ? "你正在操作：直接在上面的画面里点击、输入，检查无误后亲手提交" : "已停在最终提交前，浏览器保持登录"}</b>
          <span className="small muted">{run?.takeover ? "完成后点「操作完了」，系统截图存证" : "点「我来操作」，上面的实时画面就能直接点、打字"}</span>
          <span className="spacer" />
          <HoldButtons task={task} review={task.pendingReview} takeover={!!run?.takeover} onChanged={reload} />
        </div>
      </div>
    )}
    {st === "blocked" && !isScanBlockReason(task.blockReason) && task.learningRun && user.role === "admin" && (
        <div className="card attn-scan"><ExplorationQuestion task={task} question={run?.question} run={run} onChanged={reload} /></div>
      )}
      {/* 扫码放在所有标签页外面：不管看哪个标签都能看到、弹框都能弹出（之前在「操作」标签里，切到别的标签弹框被一起藏起来） */}
      {st === "waiting_scan" && (
        <div className="card attn-scan">
          <ScanPanel taskId={task.id} canAct={staff} who={task.assigneeLabel ?? null} />
        </div>
      )}

      <div hidden={curTab !== "overview"}>
      {/* 顶部浅蓝块 */}
      <div className={`detail-head ${cardClass}`} data-testid="detail-head">
        <div className="row overview-recovery"><RecoveryControls task={task} canAct={staff} onChanged={reload} /></div>
        <Link to="/" className="back">
          ← 返回列表
        </Link>
        <div className="row">
          <h1>
            #{task.no} <span translate="no">{task.title}</span>
          </h1>
          <StatusBadge status={st} />
          <PriorityBadge priority={task.priority} />
          {task.dryRun && <DryBadge />}
          {task.learningRun && <Badge tone="default">探索运行</Badge>}
          <AssigneePicker task={task} onChanged={reload} />
        </div>
        <div className="meta">
          {task.typeName} · <span translate={task.clientEntity.name ? "no" : undefined}>{task.clientEntity.name || "（未填公司名）"}</span>
          {task.clientEntity.uen && ` · UEN ${task.clientEntity.uen}`} · <span translate="no">{task.createdBy}</span> 建于{" "}
          <span title={fmtDateTime(task.createdAt)}>{fmtTime(task.createdAt)}</span>
          {task.createdVia === "miniprogram" && "（小程序）"}
        </div>
        {staff && sponsorEmailOf(task) && <SponsorMailLine email={sponsorEmailOf(task)!} />}
        {task.progress && !["draft", "ready", "queued"].includes(st) && (
          <div style={{ marginTop: 12 }} data-testid="progress">
            <ProgressBar k={task.progress.k} n={task.progress.n} big />
            <div className="row" style={{ marginTop: 6 }}>
              <b>
                第 {task.progress.k}/{task.progress.n} 步：<span translate="no">{task.progress.label}</span>
              </b>
              {task.etaMinutes !== null && <span className="muted">预计还要 {task.etaMinutes} 分钟</span>}
            </div>
          </div>
        )}
        {task.timing?.queuedAt && (
          <div className="timing-grid" data-testid="timing-detail">
            <div>
              <span className="muted small">安排（入队）</span>
              <b title={fmtDateTime(task.timing.queuedAt)}>{fmtTime(task.timing.queuedAt)}</b>
            </div>
            <div>
              <span className="muted small">等待开始</span>
              <b>{task.timing.waitMs !== null ? fmtDur(task.timing.waitMs) : "—"}</b>
            </div>
            <div>
              <span className="muted small">{task.timing.inProgress ? "已执行" : "执行用时"}</span>
              <b>{task.timing.runMs !== null ? fmtDur(task.timing.runMs) : "—"}</b>
            </div>
            <div>
              <span className="muted small">其中 AI 在跑</span>
              <b>{task.timing.aiMs !== null ? fmtDur(task.timing.aiMs) : "—"}</b>
            </div>
            <div>
              <span className="muted small">其中等人（扫码/审核/卡住）</span>
              <b>{task.timing.humanMs !== null ? fmtDur(task.timing.humanMs) : "—"}</b>
            </div>
            {task.timing.endedAt && (
              <div>
                <span className="muted small">结束</span>
                <b title={fmtDateTime(task.timing.endedAt)}>{fmtTime(task.timing.endedAt)}</b>
              </div>
            )}
          </div>
        )}
        {st === "draft" && task.missing.length > 0 && (
          <div className="notice warn" style={{ marginTop: 12, marginBottom: 0 }} data-testid="missing">
            还缺：{task.missing.join("、")}
          </div>
        )}
        {st === "failed" && task.blockReason && (
          <div className="notice err" style={{ marginTop: 12, marginBottom: 0 }}>
            失败原因：{task.blockReason}
          </div>
        )}
        {act.error && (
          <div className="notice err" style={{ marginTop: 12, marginBottom: 0 }}>
            {act.error}
          </div>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          {canStart && (
            <button className="btn btn-primary btn-big" data-testid="start-task" disabled={!!act.busy || !!startBlocked} title={startBlocked ?? undefined} onClick={() => void start()}>
              ▶ {st === "failed" ? "重新开始" : "开始执行任务"}
            </button>
          )}
          {["draft", "ready", "failed"].includes(st) && (
            <Link className="btn" to={`/tasks/${task.id}/prepare`}>
              📁 准备文件
            </Link>
          )}
          {!preparing && ["draft", "ready", "blocked"].includes(st) && (
            <Link className="btn" to={`/tasks/${task.id}/edit`}>
              ✎ 编辑资料 / 材料
            </Link>
          )}
          {["running", "waiting_scan", "review_required", "blocked"].includes(st) && staff && !reviewInTakeover && !run?.takeover && (
            <button className="btn" onClick={() => setLive((v) => !v)}>
              {live ? "收起实时画面" : "👁 看实时画面"}
            </button>
          )}
          <span className="spacer" />
          {!CLOSED.includes(st) && (
            <label className="row small">
              优先级
              <select
                value={task.priority}
                aria-label="优先级"
                style={{ width: "auto" }}
                disabled={!!act.busy || (task.priority === "urgent" && !canUrgent)}
                onChange={(e) => void changePriority(e.target.value as Priority)}
              >
                {(task.priority === "urgent" && !canUrgent ? (["urgent"] as Priority[]) : priorities).map((p) => (
                  <option key={p} value={p}>{PRIORITY_TEXT[p]}</option>
                ))}
              </select>
            </label>
          )}
          {!CLOSED.includes(st) && (
            <button className="btn btn-danger" disabled={!!act.busy} onClick={() => void cancel()}>
              取消任务
            </button>
          )}
          {st !== "archived" && staff && (
            <button className="btn" disabled={!!act.busy} onClick={() => void remove()} data-testid="remove-task">
              删除任务
            </button>
          )}
        </div>
      </div>

      </div>
      <div hidden={curTab !== "controls"}>
      {/* 扫码 */}
      {st === "blocked" && /分钟无人扫码/.test(task.blockReason ?? "") && staff && <ScanAutoRelogin taskId={task.id} onDone={reload} />}
      {st === "blocked" && isScanBlockReason(task.blockReason) && staff && <div className="card">
        <div className="notice warn">{/分钟无人扫码/.test(task.blockReason ?? "") ? "扫码等待已超时，可重新登录获取新的二维码。" : `${task.blockReason}。确认账号授权后可重新登录。`}</div>
        <button className="btn btn-primary" disabled={!!act.busy} onClick={() => void act.run("scan-restart", async () => { await api.scanRestart(task.id); await reload(); return { ok: true }; })}>重新登录</button>
      </div>}

      {/* 检查点审核 */}
      {task.pendingReview && st === "review_required" && (
        <div className="card attn-review">
          <ReviewPanel
            externalScreen
            task={task}
            review={task.pendingReview}
            user={user}
            labels={labels}
            takeover={!!run?.takeover}
            onChanged={reload}
          />
        </div>
      )}

      {/* 卡住 */}
      {st === "blocked" && (
        <div className="card attn-blocked">
          <StuckPanel externalScreen task={task} takeover={!!run?.takeover} lastShot={lastShot} canAct={staff} onChanged={reload} />
        </div>
      )}

      {/* 结果 */}
      {st === "done" && task.result && (
        <div className="card card-ok" data-testid="result">
          <h2 className="ok-text">✅ 已完成</h2>
          <dl className="kv">
            <dt>回执号</dt>
            <dd>
              <b style={{ fontSize: 24 }} data-testid="receipt">{task.result.receiptNo ?? "（无）"}</b>
            </dd>
            <dt>说明</dt>
            <dd translate="no">{task.result.summary}</dd>
          </dl>
          {(task.result.documents ?? []).length > 0 && (
            <div className="row" style={{ marginTop: 12, gap: 8 }} data-testid="result-docs">
              {(task.result.documents ?? []).map((d) => {
                const href = mediaUrl(d.url, token);
                return href ? (
                  <a key={d.url} className="btn" href={href} target="_blank" rel="noreferrer" download>
                    📄 {d.label}
                  </a>
                ) : null;
              })}
            </div>
          )}
          {task.result.screenshots.length > 0 && (
            <div className="row" style={{ marginTop: 12 }}>
              {task.result.screenshots.map((s) => {
                const src = mediaUrl(s, token);
                if (!src) return null;
                return <img key={s} src={src} alt="回执截图" className="shot" style={{ width: 260 }} onClick={() => lb.open(src)} />;
              })}
            </div>
          )}
        </div>
      )}

      {/* AI 建议 */}
      {propQ.data && <ProposalCard p={propQ.data} role={user.role} onChanged={reload} showType={false} />}

      </div>

      {curTab === "steps" && (
        <>
          <div className="card">
            {run ? (
              <>
                <h2>
                  步骤 <span className="small muted">（第 {run.attemptNo} 次运行{run.dryRun ? "，dry run" : ""}）</span>
                </h2>
                <StepTimeline run={run} />
              </>
            ) : (
              <div className="empty">还没有开始运行，开始后这里会逐步显示进度和截图</div>
            )}
          </div>
          {showRecording && run && (
            <div className="card">
              <h2>录像回放</h2>
              <iframe className="player" title="运行录像" src={playerSrc} data-testid="player" />
            </div>
          )}
        </>
      )}

      {preparing && curTab === "files" && <><ExplorationPreparation key={task.id} task={task} onStarted={reload} /><ExtractionPanel task={task} canEdit onChanged={reload} /></>}
      {!preparing && curTab === "files" && (
        <>
        {(typeQ.data?.manifestSchema.files.length ?? 0) > 0 && <FileBoard task={task} canUpload={canEditMaterials} onChanged={reload} />}
        <div className="card">
          <div className="row"><h2 style={{ margin: 0 }}>材料</h2><span className="spacer" />{staff && st !== "queued" && <RestartTaskButton task={task} onChanged={reload} primary={st === "blocked" || st === "failed"} />}</div>
          <MaterialsPanel
            taskId={task.id}
            reqs={typeQ.data?.manifestSchema.files ?? []}
            files={task.files}
            canUpload={canEditMaterials}
            canRemove={canEditMaterials}
            onUploaded={reload}
          />
          {!canEditMaterials && !CLOSED.includes(st) && <p className="muted small">任务执行中不能删除材料；可以补传新版本，执行端会收到通知。</p>}
          {task.materialsNote && <p className="pre" translate="no" style={{ marginTop: 12 }}>{task.materialsNote}</p>}
          {startBlocked && RESTARTABLE_HINT.includes(st) && <p className="notice">{startBlocked}</p>}
        </div>
        <ExtractionPanel task={task} canEdit={canEditMaterials} onChanged={reload} />
        </>
      )}

      {curTab === "data" && <TaskData task={task} labels={labels} />}

      {curTab === "sop" && <>
        {user.role === "admin" && <div className="card"><SopSupplements typeId={task.typeId} taskId={task.id} /></div>}
        <details className="card"><summary>逐步意见（旧方式：按步骤写意见整理成 SOP）</summary>{run ? <ExecutionJournalHistory task={task} run={run} canEdit={user.role === "admin"} onChanged={reload} /> : <div className="empty">运行后可在这里整理 SOP</div>}</details>
      </>}
      {curTab === "points" && staff && <RecoveryPoints task={task} canAct={staff} onChanged={reload} />}
      {lb.node}
    </TaskWorkspace>
  );
}

function TaskData({ task, labels }: { task: TaskDTO; labels: Record<string, string> }) {
  const entries = Object.entries(task.manifest);
  return (
    <div className="card">
      <h2>任务资料</h2>
      {!entries.length && <div className="empty">这个任务没有填写资料字段</div>}
      <dl className="kv">
        {entries.map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <dt>{labels[k] ?? k}</dt>
            <dd translate={typeof v === "boolean" ? undefined : "no"}>{typeof v === "boolean" ? (v ? "是" : "否") : String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** 等扫码超时卡住的任务：有人打开就自动重新登录（回原服务拿新二维码），扫码弹框接着出现，不用人点 */
function ScanAutoRelogin({ taskId, onDone }: { taskId: string; onDone: () => void }) {
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (done.current === taskId) return;
    done.current = taskId;
    void api.scanRestart(taskId, true).then(onDone, () => undefined);
  }, [taskId, onDone]);
  return null;
}
